import type { TransactionFailedProperties } from '@/ampli';
import { formatUnits } from 'viem';

import type { AdvancedOrderProvider } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import type { Price, Quote } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { getUsdDollarAmountBucket } from '@/utils/amplitude';
import { amountToUsd } from '@/utils/numbers';

import type { TokenInputValues } from '../store';
import type { TokenUsdPrices } from '../tradeQuote';
import type { ExecutionContext } from './types';

/** Shared Amplitude token fields emitted by submit lifecycle events. */
export type TokenEventFields = {
  buy_token_address: string;
  buy_token_amount: number;
  buy_token_amount_bucket: string;
  buy_token_amount_usd: number;
  buy_token_chain_id: number;
  buy_token_chain_name: string;
  buy_token_symbol: string | undefined;
  sell_token_address: string;
  sell_token_amount: number;
  sell_token_amount_bucket: string;
  sell_token_amount_usd: number;
  sell_token_chain_id: number;
  sell_token_chain_name: string;
  sell_token_symbol: string | undefined;
};

type InputTokenEventArgs = {
  source: 'input';
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellInput: TokenInputValues;
  buyInput: TokenInputValues;
};

type QuoteTokenEventArgs = {
  source: 'quote';
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellAmount: string;
  buyAmount: string;
  tokenUsdPrices: TokenUsdPrices;
};

/** Builds the shared token fields without changing legacy input and quote amount semantics. */
export function buildTokenEventFields(
  args: InputTokenEventArgs | QuoteTokenEventArgs,
): TokenEventFields {
  const sellAmountUsd =
    args.source === 'input'
      ? args.sellInput.usd
      : amountToUsd(args.sellAmount, args.sellToken.decimals, args.tokenUsdPrices.sellTokenUsd);
  const buyAmountUsd =
    args.source === 'input'
      ? args.buyInput.usd
      : amountToUsd(args.buyAmount, args.buyToken.decimals, args.tokenUsdPrices.buyTokenUsd);
  const sellAmount =
    args.source === 'input'
      ? parseFloat(args.sellInput.decimal) || 0
      : parseFloat(formatUnits(BigInt(args.sellAmount), args.sellToken.decimals));
  const buyAmount =
    args.source === 'input'
      ? parseFloat(args.buyInput.decimal) || 0
      : parseFloat(formatUnits(BigInt(args.buyAmount), args.buyToken.decimals));

  return {
    buy_token_address: args.buyToken.address,
    buy_token_amount: buyAmount,
    buy_token_amount_bucket: getUsdDollarAmountBucket(buyAmountUsd),
    buy_token_amount_usd: buyAmountUsd,
    buy_token_chain_id: args.buyToken.chainId,
    buy_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[args.buyToken.chainId] ?? '',
    buy_token_symbol: args.buyToken?.symbol,
    sell_token_address: args.sellToken.address,
    sell_token_amount: sellAmount,
    sell_token_amount_bucket: getUsdDollarAmountBucket(sellAmountUsd),
    sell_token_amount_usd: sellAmountUsd,
    sell_token_chain_id: args.sellToken.chainId,
    sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[args.sellToken.chainId] ?? '',
    sell_token_symbol: args.sellToken.symbol,
  };
}

/** Applies the legacy referrer expiry check at the point an event or record is built. */
export function matchaReferrer(ctx: Pick<ExecutionContext, 'referrer'>): string | undefined {
  return !ctx.referrer.isExpired() && ctx.referrer.id ? ctx.referrer.id : undefined;
}

/** Effective slippage captured by the submitted quote. */
export type SubmittedSlippage = {
  bps: number | undefined;
  mode: 'auto' | 'manual' | undefined;
};

/** Reads the effective slippage from the submitted quote rather than current settings. */
export function slippageFromQuote(raw: Price | Quote): SubmittedSlippage {
  if ('maxSlippageBps' in raw) {
    return { bps: raw.maxSlippageBps, mode: raw.appliedSlippageMode };
  }
  return {
    bps: 'appliedSlippageBps' in raw ? raw.appliedSlippageBps : undefined,
    mode: 'appliedSlippageMode' in raw ? raw.appliedSlippageMode : undefined,
  };
}

type FailureEventToken = {
  address: string;
  chainId: number;
  symbol?: string;
};

/** Normalized transaction-failure analytics payload. */
export type FailureEvent = {
  sellToken: FailureEventToken;
  buyToken: FailureEventToken;
  failureStage: TransactionFailedProperties['failure_stage'];
  failureReason: TransactionFailedProperties['failure_reason'];
  swapOrderType: TransactionFailedProperties['swap_order_type'];
  swapExecutionType?: TransactionFailedProperties['swap_execution_type'];
  swapTxHash?: string;
  revertReason?: string;
  submissionReason?: string;
  recipientAddress?: string;
  matchaReferrer?: string;
  swapSlippageBps?: number;
  swapSlippageMode?: TransactionFailedProperties['swap_slippage_mode'];
  swapProvider?: TransactionFailedProperties['swap_provider'];
  bridgeProvider?: string;
  advancedOrderProvider?: AdvancedOrderProvider;
};
